import React, { useState, useContext, useEffect, useRef } from "react";
import { getCsrf } from "../api";
import { authApi } from "../services/auth";
import type { LoginResponse } from "../types/auth";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { User } from "../types/User";
import "./LoginPage.css";
import { FaTimes, FaEye, FaEyeSlash, FaEnvelope } from "react-icons/fa";
import GoogleButton from "../components/GoogleButton";
import AppleButton from "../components/AppleButton";
import DesktopInstallBanner, { markHasDesktopApp } from "../components/DesktopInstallBanner";
import { GOOGLE_CLIENT_ID } from "../config";

import { Capacitor } from "@capacitor/core";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
// Face ID / Touch ID sign-in — device keys (see src/lib/biometricSignIn.ts).
// This page no longer stores the raw email + password in the keychain; the
// key is issued after any successful sign-in by BiometricEnrollPrompt (App).
import {
  canOfferBiometricLogin,
  getBiometricAvailability,
  loginWithBiometrics,
  shouldAutoPromptBiometric,
  BiometricError,
} from "../lib/biometricSignIn";
import { safeInternalPath } from "../utils/safeInternalPath";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface FormData {
  email: string;
  password: string;
}

// Login response flags. The server's login handlers do a live Stripe
// status sync for talents with stripeAccountId && !isStripeOnboarded; the
// flags tell the client where to route after login:
//   needsStripeFinish → /finish-stripe  (DJ didn't finish onboarding; can retry)
//   stripeRejected    → /stripe-rejected (terminal — KYC fail, sanctions, etc.)
//   stripeRejectionCode → diagnostic reference, displayed muted on the rejected page
// Login itself is unconditional in all three cases — the user IS logged in.
//
// `stripeUrl` was returned by the old auto-defer behaviour; mandatory Stripe removed
// that, so it's never present on a login response anymore. Kept off the type.

// Where to send the talent after login when there's no Stripe-recovery
// detour. Honors a ?next= deep link (e.g. /requests from the new-request SMS)
// so a logged-out talent who taps the SMS lands on their requests after a
// single login instead of being dumped on their profile. Only internal
// absolute paths are allowed — protocol-relative ("//evil.com") and absolute
// URLs are rejected to prevent an open-redirect. Default = Requests with the
// Profile drawer open over it ("#profile"), the drawer-era equivalent of the
// old /profile landing.
const AFTER_LOGIN_DEFAULT = "/requests#profile";
function safeNext(raw: string | null): string {
  if (!raw) return AFTER_LOGIN_DEFAULT;
  let path: string;
  try {
    path = decodeURIComponent(raw);
  } catch {
    return AFTER_LOGIN_DEFAULT;
  }
  // Same-origin paths only. See utils/safeInternalPath for the backslash case.
  return safeInternalPath(path, AFTER_LOGIN_DEFAULT);
}

// Shared routing helper. Three login surfaces (email, Google, Apple +
// biometric) all need the same post-login navigation logic. Centralizing
// keeps them in lockstep when we add another flag later.
function routeAfterLogin(
  data: LoginResponse,
  login: (user: User, token: string) => void,
  navigate: (path: string, options?: any) => void,
  fallback: string = AFTER_LOGIN_DEFAULT
): boolean {
  if (!data.accessToken || !data.user) return false;
  if (data.stripeRejected) {
    login(data.user, data.accessToken);
    navigate("/stripe-rejected", { state: { code: data.stripeRejectionCode } });
    return true;
  }
  // FREE TIER: needsStripeFinish no longer diverts the login. The talent
  // lands in the app with free requests; money buttons open "Connect your bank".
  login(data.user, data.accessToken);
  // replace: the login form shouldn't sit under the app in history — with the
  // Profile drawer landing ("/requests#profile"), a plain back would
  // otherwise bounce a just-logged-in talent straight back to /login.
  navigate(fallback, { replace: true });
  return true;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get("next"));
  const { login } = useContext(AuthContext);
  const [form, setForm] = useState<FormData>({ email: "", password: "" });
  const [error, setError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  // Set when the server says this email belongs to a Google/Apple-born
  // account (msg "social_login_hint") — we guide instead of dead-ending.
  const [socialHintProviders, setSocialHintProviders] = useState<string[]>([]);

  // Desktop companion sign-in bridge. Two roles, both gated so web + mobile +
  // iOS render exactly as before:
  //  - isElectron: we're INSIDE the desktop app, which is its own browser with
  //    no Google/Apple account picker or Keychain autofill → route social
  //    sign-in through the user's REAL browser and redeem the one-time code it
  //    hands back (requestapp:// deep link → /auth/desktop/exchange).
  //  - desktopHandoff (?desktop=1): we ARE that real browser, opened by the app
  //    → after any successful login, mint a handoff code (/auth/desktop/handoff)
  //    and deep-link it back instead of navigating into the web app here.
  const isElectron = !!(window as any).electronAPI?.isElectron;
  // Gate the browser-signin UI on the CAPABILITY, not just "is Electron":
  // desktop shells ≤ v0.2.0 don't expose openExternalLogin, and since the shell
  // loads the LIVE site, they'd render a button that silently no-ops. Old
  // shells keep the previous in-app buttons; shells ≥ v0.3.0 get the browser
  // flow.
  const canBrowserSignin =
    isElectron && typeof (window as any).electronAPI?.openExternalLogin === "function";
  const desktopHandoff = searchParams.get("desktop") === "1";
  // The desktop-install banner hides itself on phones, but its wrapper's top
  // padding stayed — a 16px strip of black body between the green header and
  // the page (founder decision). Same gate the banner uses, so the wrapper only
  // exists where the banner can.
  const { isDesktop: desktopPointer } = useInstallPrompt();
  // This window was opened BY the desktop app — remember that in this browser
  // so install banners never nag someone who already has the app.
  useEffect(() => {
    if (desktopHandoff) markHasDesktopApp();
    // eslint-disable-next-line
  }, [desktopHandoff]);
  const [browserSigninPending, setBrowserSigninPending] = useState(false);
  const [handoffReturning, setHandoffReturning] = useState(false);

  // Face ID / Touch ID sign-in for returning talents on this install. The
  // button shows only when a device key (or a legacy stored login) is armed
  // here AND the phone has biometrics; the label follows the hardware.
  const [hasBiometric, setHasBiometric] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState("Face ID");
  const [biometricBusy, setBiometricBusy] = useState(false);
  // Latest handler for the auto-prompt timer (declared below the effect).
  const biometricLoginRef = useRef<() => void>(() => {});
  const autoPromptedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    let timer: number | null = null;
    (async () => {
      if (!Capacitor.isNativePlatform()) return;
      const [offer, { label }] = await Promise.all([canOfferBiometricLogin(), getBiometricAvailability()]);
      if (!alive) return;
      setBiometricLabel(label);
      setHasBiometric(offer);
      // Banking-app behaviour: landing here NOT signed in fires Face ID right
      // away — no tap — unless this landing came from an explicit "Log out".
      // Once per landing: a cancelled sheet leaves the button, no re-nag.
      if (offer && !autoPromptedRef.current && shouldAutoPromptBiometric()) {
        autoPromptedRef.current = true;
        timer = window.setTimeout(() => {
          if (alive) biometricLoginRef.current();
        }, 350);
      }
    })();
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const handleBiometricLogin = async () => {
    if (biometricBusy) return;
    setBiometricBusy(true);
    setError("");
    try {
      const data = await loginWithBiometrics();
      // Routes to /finish-stripe or /stripe-rejected if the server
      // flagged the talent as mid-Stripe; otherwise /profile.
      if (!(await finishLogin(data as LoginResponse))) {
        setError(t("loginPage.unexpectedServerResponse"));
      }
    } catch (err: any) {
      const code = err instanceof BiometricError ? err.code : "failed";
      if (code === "cancelled") return; // they backed out of the sheet — say nothing
      if (code === "expired") {
        // Key revoked/expired (password reset, long idle, another device). The
        // normal doors are right below; the next sign-in re-arms this install.
        setHasBiometric(false);
        setError(t("loginPage.signInExpiredSign", { biometricLabel }));
        return;
      }
      if (code === "network") {
        setError(t("loginPage.couldnTReachRequest"));
        return;
      }
      setError(t("loginPage.didnTWorkSign", { biometricLabel }));
    } finally {
      setBiometricBusy(false);
    }
  };
  biometricLoginRef.current = handleBiometricLogin;

  // ── Desktop companion sign-in helpers (no-ops on web/mobile) ──────────────
  // In the external browser (?desktop=1): after ANY successful login, hand the
  // freshly authenticated session back to the desktop app via a short-lived
  // one-time code + requestapp:// deep link, instead of routing into the web
  // app here. Falls back to normal routing if the handoff can't be minted.
  const finishLogin = async (data: LoginResponse): Promise<boolean> => {
    if (desktopHandoff && data.accessToken) {
      try {
        const r = await authApi.desktopHandoff(data.accessToken);
        const code = r?.code;
        if (code) {
          setHandoffReturning(true);
          window.location.href = `requestapp://auth?code=${encodeURIComponent(code)}`;
          return true;
        }
      } catch {
        /* handoff mint failed — fall through to normal in-browser routing */
      }
    }
    return routeAfterLogin(data, login, navigate, nextPath);
  };

  // Inside the desktop app: open sign-in in the user's real system browser
  // (account picker + Keychain autofill live there).
  const startBrowserSignin = (provider?: string) => {
    setError("");
    (window as any).electronAPI?.openExternalLogin?.({ provider });
    setBrowserSigninPending(true);
  };

  // Inside the desktop app: redeem the one-time code the browser handed back
  // for a normal session (identical to any other login completion).
  const exchangeDesktopCode = async (code: string) => {
    try {
      const r = await authApi.desktopExchange(code);
      setBrowserSigninPending(false);
      if (!routeAfterLogin(r, login, navigate, nextPath)) {
        setError(t("loginPage.signInDidnT"));
      }
    } catch {
      setBrowserSigninPending(false);
      setError(t("loginPage.signInDidnT"));
    }
  };

  // Inside the desktop app: receive the code from the requestapp:// deep link
  // (and pick up one that arrived during cold start), then exchange it.
  useEffect(() => {
    const eapi = (window as any).electronAPI;
    if (!eapi?.isElectron) return;
    const unsub = eapi.onDesktopAuthCode?.((code: string) => {
      if (code) exchangeDesktopCode(code);
    });
    eapi
      .getPendingAuthCode?.()
      .then((code: string) => {
        if (code) exchangeDesktopCode(code);
      })
      .catch(() => {});
    return () => {
      if (typeof unsub === "function") unsub();
    };
    // Subscribe once: re-running would re-read the pending code and try to
    // exchange it twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await getCsrf();
      const res = await authApi.login(form);

      if (res.accessToken && res.user) {
        // Face ID sign-in used to stash the raw email + password in the
        // keychain right here. It now runs on a per-install device key that
        // BiometricEnrollPrompt (App) offers / re-arms after ANY successful
        // sign-in — nothing password-shaped is stored anymore.

        // Routes to /finish-stripe, /stripe-rejected, or /profile
        // based on the server's resume-Stripe flags. See routeAfterLogin.
        // In the desktop-handoff browser this mints the code + deep-links back.
        await finishLogin(res);
      } else {
        setError(t("loginPage.unexpectedServerResponse"));
      }
    } catch (err: any) {
      const msg: string = err?.response?.data?.msg || t("loginPage.loginFailed");
      if (msg === "social_login_hint") {
        // Google/Apple-born account: email+password can't work until they
        // set a password. Point them at the right button instead.
        const providers: string[] = err?.response?.data?.providers || [];
        setSocialHintProviders(providers.length ? providers : ["google"]);
        setError("");
        return;
      }
      setSocialHintProviders([]);
      setError(msg);
    }
  };

  const socialHintNames = socialHintProviders
    .map((p) => (p === "apple" ? "Apple" : "Google"))
    .join(" or ");

  const handleGoogleSuccess = async (credential: string) => {
    try {
      await getCsrf();
      const res = await authApi.googleLogin(credential);
      // See routeAfterLogin for full routing logic.
      if (!(await finishLogin(res))) {
        setError(t("loginPage.unexpectedServerResponse"));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.msg;
      if (msg === "user_not_found") {
        setError(t("loginPage.noLinkedAccountFound"));
        return;
      }
      setError(msg || t("loginPage.googleLoginFailed"));
    }
  };

  const handleAppleSuccess = async (idToken: string) => {
    try {
      await getCsrf();
      const res = await authApi.appleLogin(idToken);
      // See routeAfterLogin for full routing logic.
      if (!(await finishLogin(res))) {
        setError(t("loginPage.unexpectedServerResponse"));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.msg;
      if (msg === "user_not_found") {
        setError(t("loginPage.noLinkedAccountFound2"));
        return;
      }
      setError(msg || t("loginPage.appleLoginFailed"));
    }
  };


  // External browser (?desktop=1) finished a login and is bouncing the session
  // back to the desktop app — show a calm confirmation, not the login form.
  if (handoffReturning) {
    return (
      <div className="login-container">
        <div className="login-inner" style={{ textAlign: "center" }}>
          <h1 className="login-hero-title">{t("loginPage.youReSignedIn")}</h1>
          <p className="login-hero-tagline">{t("loginPage.takingYouBackTo")}</p>
          <p className="login-footer">{t("loginPage.youCanCloseThis")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!desktopHandoff && desktopPointer && (
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "1rem 1rem 0" }}>
          <DesktopInstallBanner hideWhenInstalled />
        </div>
      )}
      <div className="login-container">
        <div className="login-inner">
          <h1 className="login-hero-title">{t("loginPage.welcomeBack")}</h1>
          <p className="login-hero-tagline">{t("loginPage.signInToRequest")}</p>

          {error && <p className="login-error">{error}</p>}

          {socialHintProviders.length > 0 && (
            <div style={{ marginBottom: "0.9rem" }}>
              <p className="login-info" style={{ marginBottom: "0.6rem" }}>
                {t("loginPage.thisAccountSignsIn", { socialHintNames })}
              </p>
              {/* Show the real provider button(s) right here so they can sign
                  in with one tap instead of hunting for the button. */}
              <div className="social-login-stack">
                {canBrowserSignin ? (
                  // Desktop app → sign in through the user's real browser.
                  <button
                    type="button"
                    className="email-signin-btn"
                    onClick={() => startBrowserSignin()}
                  >
                    {browserSigninPending
                      ? t("loginPage.waitingForYourBrowser")
                      : t("loginPage.continueWith", { socialHintNames })}
                  </button>
                ) : (
                  <>
                    {socialHintProviders.includes("google") && GOOGLE_CLIENT_ID && (
                      <GoogleButton onSuccess={handleGoogleSuccess} text="continue_with" />
                    )}
                    {/* No REACT_APP_APPLE_CLIENT_ID gate here: that env var is
                        Vercel-only, so the gate made this button invisible in
                        native builds (≤1.1(4) bug). The server-side social hint
                        is the source of truth for showing it. */}
                    {socialHintProviders.includes("apple") && (
                      <AppleButton onSuccess={handleAppleSuccess} text="sign-in" />
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Face ID / Touch ID button — only when this install is armed */}
          {hasBiometric && !showEmailForm && (
            <button
              type="button"
              className="biometric-btn"
              onClick={handleBiometricLogin}
              disabled={biometricBusy}
              aria-busy={biometricBusy}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                <path d="M7.97 16.03a7 7 0 0 0 8.06 0" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
              </svg>
              {biometricBusy ? t("loginPage.signingIn") : t("loginPage.signInWith", { biometricLabel })}
            </button>
          )}

          {!showEmailForm && (
            <div className="social-login-stack">
              {canBrowserSignin ? (
                // Desktop app: its embedded browser has no Google/Apple account
                // picker or Keychain autofill, so sign in through the user's
                // real browser (see startBrowserSignin). Tappable while pending
                // so a cancelled/closed browser can retry.
                <button
                  type="button"
                  className="email-signin-btn"
                  onClick={() => startBrowserSignin()}
                >
                  {browserSigninPending
                    ? t("loginPage.waitingForYourBrowser")
                    : t("loginPage.continueWithGoogleOr")}
                </button>
              ) : (
                <>
                  {GOOGLE_CLIENT_ID && (
                    <GoogleButton onSuccess={handleGoogleSuccess} text="continue_with" />
                  )}
                  {/* Native builds compile on the dev Mac where the Vercel-only
                      REACT_APP_APPLE_CLIENT_ID isn't set — but AppleButton carries
                      the real client ID as its built-in fallback, so the gate only
                      applies to web. Without the native bypass the app shipped with
                      NO Apple button on this page (TestFlight 1.1(4) and earlier). */}
                  {(process.env.REACT_APP_APPLE_CLIENT_ID || Capacitor.isNativePlatform()) && (
                    <AppleButton onSuccess={handleAppleSuccess} text="sign-in" />
                  )}
                </>
              )}
              <button
                type="button"
                className="email-signin-btn"
                onClick={() => { setShowEmailForm(true); setError(""); }}
              >
                <FaEnvelope size={16} />
                {t("loginPage.signInWithEmail")}
              </button>
            </div>
          )}

          {showEmailForm && (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-wrapper">
                <input
                  type="email"
                  name="email"
                  placeholder={t("loginPage.email")}
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoFocus
                  className="login-input"
                />
                {form.email && (
                  <span
                    className="clear-icon"
                    onClick={() => setForm({ ...form, email: "" })}
                  >
                    <FaTimes />
                  </span>
                )}
              </div>

              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("loginPage.password")}
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="login-input"
                />
                <span
                  className="clear-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              <button type="submit" className="login-button">
                {t("loginPage.signIn")}
              </button>

              <button
                type="button"
                className="login-back-link"
                onClick={() => { setShowEmailForm(false); setError(""); }}
              >
                {t("loginPage.useADifferentMethod")}
              </button>
            </form>
          )}

          <p className="login-footer">
            <Link to="/forgot-password" className="login-link">{t("loginPage.forgotPassword")}</Link>
          </p>

          <p className="login-footer">
            <T k="loginPage.noAccountCreateYour" tags={{ 0: (c) => <Link to="/register" className="login-link">{c}</Link> }} />
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
